import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import type { ReactElement } from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type { GateCheckId } from "../features/mergeApproval/api";
import {
  approvalBlocker,
  buildApproveRequest,
  isGateHidden,
  isTaskStateRefusal,
  prohibitingFailures,
} from "../features/mergeApproval/logic";
import type { AcknowledgedTokens } from "../features/mergeApproval/logic";
import { MergeGatePredicates } from "../features/mergeApproval/MergeGatePredicates";
import {
  predicateLabel,
  refusalText,
} from "../features/mergeApproval/refusalText";
import { useApproveMerge } from "../features/mergeApproval/useApproveMerge";
import { useMergeGate } from "../features/mergeApproval/useMergeGate";
import type { TaskResponse } from "../features/queue/api";

// Баннер «Ожидает подтверждения слияния»: снимок гейта, предикаты по
// классам и кнопка подтверждения. Не рисуется, если чтение гейта
// отказало `403 permission_denied` или `409 not_awaiting_approval`; прочий
// отказ чтения — Alert с текстом по коду или статусу.
export function MergeApprovalBanner({
  task,
}: {
  readonly task: TaskResponse;
}): ReactElement | null {
  const { t } = useTranslation();
  const gateQuery = useMergeGate(task.id);
  const approveMutation = useApproveMerge(task.id);
  const [acknowledged, setAcknowledged] = useState<AcknowledgedTokens>({});
  const blockerId = useId();

  if (gateQuery.isError && isGateHidden(gateQuery.error)) {
    return null;
  }
  // Первичная загрузка: пока неизвестно, прочитан гейт или отказал по
  // праву, баннер не рисуется, чтобы не мигать заголовком без права.
  if (!gateQuery.data && !gateQuery.isError) {
    return null;
  }

  const handleAcknowledge = (
    id: GateCheckId,
    stateToken: string,
    checked: boolean,
  ): void => {
    setAcknowledged((prev) => {
      const next: Partial<Record<GateCheckId, string>> = { ...prev };
      if (checked) {
        next[id] = stateToken;
      } else {
        delete next[id];
      }
      return next;
    });
  };

  const handleRefresh = (): void => {
    if (approveMutation.isError) {
      approveMutation.reset();
    }
    void gateQuery.refetch();
  };

  // Последнее чтение ошибочно — старый снимок не показываем как
  // актуальный: остаются ошибка и «Обновить состояние».
  const gate = gateQuery.isError ? undefined : gateQuery.data;
  const blocker = gate ? approvalBlocker(gate, acknowledged) : null;
  const failures = gate ? prohibitingFailures(gate) : [];

  return (
    <Alert color="teal" title={t("mergeApproval.bannerTitle")}>
      <Stack gap="sm">
        <Text>{t("mergeApproval.bannerBody")}</Text>
        {gateQuery.isError ? (
          <Alert color="red" title={t("mergeApproval.gateErrorTitle")}>
            {refusalText(gateQuery.error, t)}
          </Alert>
        ) : null}
        {gate ? (
          <>
            <Text>
              {t("mergeApproval.headSha")}:{" "}
              <Text span ff="monospace">
                {gate.head_sha}
              </Text>
            </Text>
            <Text>
              {t("mergeApproval.baseBranch")}:{" "}
              <Text span ff="monospace">
                {gate.base_branch}
              </Text>
            </Text>
            <Text>
              {t("mergeApproval.reviewedHeadSha")}:{" "}
              <Text span ff="monospace">
                {gate.reviewed_head_sha ?? "—"}
              </Text>
            </Text>
            {gate.pr_state === "merged" ? (
              <Text>{t("mergeApproval.prMerged")}</Text>
            ) : null}
            <Text>
              {gate.open_acceptance_count > 0
                ? t("mergeApproval.acceptanceOpen", {
                    count: gate.open_acceptance_count,
                  })
                : t("mergeApproval.acceptanceNone")}
            </Text>
            {gate.pr_state === "open" && failures.length > 0 ? (
              <Alert color="red" title={t("mergeApproval.prohibitingTitle")}>
                <Stack gap={0}>
                  {failures.map((p) => (
                    <Text key={p.id}>{predicateLabel(p.id, t)}</Text>
                  ))}
                </Stack>
              </Alert>
            ) : null}
            <MergeGatePredicates
              gate={gate}
              acknowledged={acknowledged}
              onAcknowledgeChange={handleAcknowledge}
              disabled={approveMutation.isPending}
            />
            {blocker !== null ? (
              <Text id={blockerId} c="dimmed" size="sm">
                {t(`mergeApproval.blocker.${blocker}`)}
              </Text>
            ) : null}
          </>
        ) : null}
        {approveMutation.isError &&
        !isTaskStateRefusal(approveMutation.error) ? (
          <Alert color="red" title={t("mergeApproval.approveErrorTitle")}>
            {refusalText(approveMutation.error, t)}
          </Alert>
        ) : null}
        {approveMutation.isSuccess ? (
          <Text c="green" size="sm">
            {t("mergeApproval.approveSucceeded")}
          </Text>
        ) : null}
        <Group>
          {gate ? (
            <Button
              color="green"
              loading={approveMutation.isPending}
              disabled={
                blocker !== null ||
                gateQuery.isFetching ||
                approveMutation.isSuccess
              }
              aria-describedby={blocker !== null ? blockerId : undefined}
              onClick={() => {
                approveMutation.mutate(buildApproveRequest(gate, acknowledged));
              }}
            >
              {approveMutation.isPending
                ? t("mergeApproval.approveInFlight")
                : t("mergeApproval.approveButton")}
            </Button>
          ) : null}
          <Button
            variant="default"
            disabled={approveMutation.isPending}
            onClick={handleRefresh}
          >
            {t("mergeApproval.refreshButton")}
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
